/* eslint-disable no-restricted-syntax */
/* eslint-disable newline-before-return */
import { findLeafObjects } from "@/components/FormEngine/helpers/findLeafObjects";
import { RawField } from "@/components/FormEngine/types";

// ========================================
// TYPES ET INTERFACES
// ========================================

/**
 * Map aplatie des champs du formulaire
 * Clé : chemin du champ sous forme de string (ex: "section.subsection.field")
 * Valeur : données brutes du champ
 */
type FlatFieldMap = Record<string, RawField>;

/**
 * Nœud feuille retourné par findLeafObjects
 */
interface LeafNode {
  /** Les données du champ */
  data: RawField;
  /** Le chemin vers ce champ sous forme de tableau */
  path: string[];
}

/**
 * Type pour l'objet JSON racine
 * Peut être n'importe quelle structure JSON valide
 */
type JsonRoot = any;

// ========================================
// FONCTION PRINCIPALE
// ========================================

/**
 * Construit une map aplatie de tous les champs d'une structure JSON hiérarchique
 *
 * Cette fonction transforme une structure arborescente complexe en une map plate
 * où chaque champ est accessible directement via son chemin complet.
 *
 * Processus :
 * 1. Parcourt l'arbre JSON pour trouver tous les nœuds feuilles (champs)
 * 2. Convertit le chemin de chaque champ en clé string
 * 3. Crée une map indexée par ces clés pour un accès rapide
 *
 * Cette transformation est utile pour :
 * - Accéder rapidement à n'importe quel champ par son chemin
 * - Éviter de parcourir l'arbre à chaque fois
 * - Simplifier les opérations de mise à jour des champs
 *
 * @param rootJsonObject - L'objet JSON racine contenant la structure hiérarchique
 * @returns Une map plate de tous les champs, indexée par leur chemin
 *
 * @example
 * // Structure hiérarchique d'entrée
 * const formData = {
 *   personalInfo: {
 *     name: {
 *       type: "text",
 *       value: "John",
 *       required: true
 *     },
 *     address: {
 *       street: {
 *         type: "text",
 *         value: "123 Main St"
 *       },
 *       city: {
 *         type: "text",
 *         value: "Paris"
 *       }
 *     }
 *   }
 * };
 *
 * const flatMap = buildFlatFieldMap(formData);
 *
 * // Résultat : map plate
 * {
 *   "personalInfo.name": { type: "text", value: "John", required: true },
 *   "personalInfo.address.street": { type: "text", value: "123 Main St" },
 *   "personalInfo.address.city": { type: "text", value: "Paris" }
 * }
 *
 * // Accès direct à un champ
 * const nameField = flatMap["personalInfo.name"];
 */
export const buildFlatFieldMap = (rootJsonObject: JsonRoot): FlatFieldMap => {
  // Étape 1 : Trouver tous les nœuds feuilles dans l'arbre
  const leafNodes = extractAllLeafNodes(rootJsonObject);

  // Étape 2 : Construire la map plate à partir des nœuds feuilles
  const flatFieldMap = createFlatMapFromLeafNodes(leafNodes);

  return flatFieldMap;
};

// ========================================
// EXTRACTION DES NŒUDS FEUILLES
// ========================================

/**
 * Extrait tous les nœuds feuilles de la structure JSON
 *
 * Un nœud feuille est un objet qui ne contient pas d'autres objets
 * comme enfants (mais peut contenir des primitives).
 *
 * @param rootJsonObject - L'objet JSON racine à parcourir
 * @returns Liste de tous les nœuds feuilles avec leurs chemins
 */
function extractAllLeafNodes(rootJsonObject: JsonRoot): LeafNode[] {
  // Utilise la fonction helper pour parcourir l'arbre récursivement
  const leafNodes = findLeafObjects(rootJsonObject);

  return leafNodes;
}

// ========================================
// CONSTRUCTION DE LA MAP PLATE
// ========================================

/**
 * Crée une map plate à partir d'une liste de nœuds feuilles
 *
 * @param leafNodes - Liste des nœuds feuilles à indexer
 * @returns Map plate indexée par les chemins
 */
function createFlatMapFromLeafNodes(leafNodes: LeafNode[]): FlatFieldMap {
  const flatMap: FlatFieldMap = {};

  // Traiter chaque nœud feuille
  for (const leafNode of leafNodes) {
    // Créer la clé et ajouter à la map
    addLeafNodeToMap(flatMap, leafNode);
  }

  return flatMap;
}

/**
 * Ajoute un nœud feuille à la map plate
 *
 * @param flatMap - La map à laquelle ajouter le nœud
 * @param leafNode - Le nœud feuille à ajouter
 */
function addLeafNodeToMap(flatMap: FlatFieldMap, leafNode: LeafNode): void {
  // Construire la clé à partir du chemin
  const mapKey = createKeyFromPath(leafNode.path);

  // Ajouter le champ à la map avec sa clé
  flatMap[mapKey] = leafNode.data;
}

/**
 * Crée une clé string à partir d'un chemin tableau
 *
 * @param path - Le chemin sous forme de tableau de strings
 * @returns La clé sous forme de string avec séparateur point
 *
 * @example
 * createKeyFromPath(["form", "section", "field"])
 * // Retourne: "form.section.field"
 */
function createKeyFromPath(path: string[]): string {
  return path.join(".");
}

// ========================================
// FONCTIONS UTILITAIRES EXPORTÉES
// ========================================

/**
 * Récupère un champ spécifique depuis une map plate
 *
 * @param flatMap - La map plate des champs
 * @param fieldPath - Le chemin du champ à récupérer
 * @returns Le champ trouvé ou undefined
 *
 * @example
 * const field = getFieldFromFlatMap(flatMap, ["personal", "name"]);
 */
export function getFieldFromFlatMap(
  flatMap: FlatFieldMap,
  fieldPath: string[],
): RawField | undefined {
  const key = createKeyFromPath(fieldPath);
  return flatMap[key];
}

/**
 * Vérifie si un champ existe dans la map plate
 *
 * @param flatMap - La map plate des champs
 * @param fieldPath - Le chemin du champ à vérifier
 * @returns true si le champ existe
 *
 * @example
 * if (hasFieldInFlatMap(flatMap, ["personal", "name"])) {
 *   // Le champ existe
 * }
 */
export function hasFieldInFlatMap(
  flatMap: FlatFieldMap,
  fieldPath: string[],
): boolean {
  const key = createKeyFromPath(fieldPath);
  return key in flatMap;
}

/**
 * Obtient toutes les clés (chemins) de la map plate
 *
 * @param flatMap - La map plate des champs
 * @returns Liste de toutes les clés
 *
 * @example
 * const allPaths = getAllFieldPaths(flatMap);
 * // ["personal.name", "personal.address", ...]
 */
export function getAllFieldPaths(flatMap: FlatFieldMap): string[] {
  return Object.keys(flatMap);
}

/**
 * Obtient le nombre de champs dans la map plate
 *
 * @param flatMap - La map plate des champs
 * @returns Le nombre de champs
 */
export function getFieldCount(flatMap: FlatFieldMap): number {
  return Object.keys(flatMap).length;
}

/**
 * Filtre les champs de la map selon un prédicat
 *
 * @param flatMap - La map plate des champs
 * @param predicate - Fonction de filtrage
 * @returns Nouvelle map contenant uniquement les champs filtrés
 *
 * @example
 * // Garder uniquement les champs requis
 * const requiredFields = filterFields(flatMap, (field) => field.required === true);
 */
export function filterFields(
  flatMap: FlatFieldMap,
  predicate: (field: RawField, path: string) => boolean,
): FlatFieldMap {
  const filteredMap: FlatFieldMap = {};

  for (const [path, field] of Object.entries(flatMap)) {
    if (predicate(field, path)) {
      filteredMap[path] = field;
    }
  }

  return filteredMap;
}

/**
 * Transforme une map plate en structure hiérarchique
 * (Fonction inverse de buildFlatFieldMap)
 *
 * @param flatMap - La map plate à transformer
 * @returns Un objet hiérarchique reconstruit
 *
 * @example
 * const hierarchical = unflattenFieldMap({
 *   "personal.name": { type: "text", value: "John" },
 *   "personal.age": { type: "number", value: 30 }
 * });
 * // Résultat:
 * // {
 * //   personal: {
 * //     name: { type: "text", value: "John" },
 * //     age: { type: "number", value: 30 }
 * //   }
 * // }
 */
export function unflattenFieldMap(flatMap: FlatFieldMap): any {
  const result: any = {};

  for (const [flatPath, fieldData] of Object.entries(flatMap)) {
    const pathSegments = flatPath.split(".");
    setNestedValue(result, pathSegments, fieldData);
  }

  return result;
}

/**
 * Définit une valeur dans un objet imbriqué en créant la structure si nécessaire
 *
 * @param obj - L'objet à modifier
 * @param path - Le chemin vers la propriété
 * @param value - La valeur à définir
 */
function setNestedValue(obj: any, path: string[], value: any): void {
  let current = obj;

  // Parcourir le chemin sauf le dernier élément
  for (let i = 0; i < path.length - 1; i++) {
    const segment = path[i];

    // Créer l'objet intermédiaire si nécessaire
    if (!(segment in current)) {
      current[segment] = {};
    }

    current = current[segment];
  }

  // Définir la valeur finale
  const lastSegment = path[path.length - 1];
  current[lastSegment] = value;
}
